import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import persistReducer from "redux-persist/es/persistReducer";
import {
  CITY_EXPORT_URL,
  CITY_IMPORT_URL,
  CITY_SAMPLE_URL,
  CREATE_CITY_URL,
  DELETE_CITY_URL,
  GET_CITIES_URL,
  UPDATE_CITY_URL,
} from "../../Services/endpoints";
import { createMasterImportThunks } from "../../Utils/createMasterImportThunks";
import { errorHandler, successToast } from "../../Utils/toast";
import { _delete, _get, _patch, _post } from "../../Services/baseApi";
import storage from "redux-persist/lib/storage";

const initialValue = {
  isLoading: false,
  cities: [],
  total: 0,
  page: 1,
  limit: 10,
};

const citySlice = createSlice({
  name: "city",
  initialState: initialValue,
  reducers: {
    setCities: (state, action) => {
      state.cities = action?.payload;
    },
  },
  extraReducers: (builder) => {
    // ADD CITY
    builder.addCase(createCityThunk.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(createCityThunk.fulfilled, (state) => {
      state.isLoading = false;
    });
    builder.addCase(createCityThunk.rejected, (state, action) => {
      state.isLoading = false;
      console.error("Create City Error:", action?.payload);
    });

    // GET CITIES
    builder.addCase(getCitiesThunk.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getCitiesThunk.fulfilled, (state, action) => {
      state.cities = action?.payload?.result?.findCities || [];
      state.total = action?.payload?.result?.totalCount;
      state.page = action?.payload?.result?.totalPageLength;
      state.limit = action?.payload?.result?.limit;
      state.isLoading = false;
    });
    builder.addCase(getCitiesThunk.rejected, (state, action) => {
      state.isLoading = false;
      console.error("Get Cities Error:", action?.payload);
    });

    // DELETE CITY
    builder.addCase(deleteCityThunk.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(deleteCityThunk.fulfilled, (state, action) => {
      state.cities = state.cities.filter((item) => item.id !== action?.payload);
      state.isLoading = false;
    });
    builder.addCase(deleteCityThunk.rejected, (state, action) => {
      state.isLoading = false;
      console.error("Delete City Error:", action?.payload);
    });

    // UPDATE CITY
    builder.addCase(updateCityThunk.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(updateCityThunk.fulfilled, (state) => {
      state.isLoading = false;
    });
    builder.addCase(updateCityThunk.rejected, (state, action) => {
      state.isLoading = false;
      console.error("Update City Error:", action?.payload);
    });
  },
});

export default persistReducer({ key: "city", storage }, citySlice.reducer);

// ------------------- Thunks -------------------

export const createCityThunk = createAsyncThunk(
  "citySlice/createCityThunk",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await _post(CREATE_CITY_URL, {
        name: payload?.name,
        stateId: payload?.stateId,
      });
      if (response?.data) {
        successToast(response.data.message || "City created successfully");
        payload?.handleFun();
        return response.data;
      }
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getCitiesThunk = createAsyncThunk(
  "citySlice/getCitiesThunk",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await _get(GET_CITIES_URL, {
        page: payload?.page,
        limit: payload?.limit,
        name: payload?.searchText || "",
        key: payload?.sortColumn,
        keyOrder: payload?.sortDirection,
        stateId: payload?.stateId || undefined,
      });
      if (response?.data) {
        return response.data;
      }
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateCityThunk = createAsyncThunk(
  "citySlice/updateCityThunk",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await _patch(UPDATE_CITY_URL, {
        id: payload?.id,
        name: payload?.name,
        stateId: payload?.stateId,
      });
      if (response?.data) {
        successToast(response.data.message || "City updated successfully");
        payload?.handleFun();
        return response.data;
      }
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const cityImportThunks = createMasterImportThunks({
  exportType: "citySlice",
  exportUrl: CITY_EXPORT_URL,
  importUrl: CITY_IMPORT_URL,
  sampleUrl: CITY_SAMPLE_URL,
  defaultExportFilename: "cities-export.xlsx",
  defaultSampleFilename: "cities-sample.xlsx",
  exportSuccessMessage: "Cities exported successfully",
});

export const exportCitiesThunk = cityImportThunks.exportThunk;
export const downloadCitySampleThunk = cityImportThunks.downloadSampleThunk;
export const importCitiesThunk = cityImportThunks.importThunk;

export const deleteCityThunk = createAsyncThunk(
  "citySlice/deleteCityThunk",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await _delete(DELETE_CITY_URL + `/${payload?.id}`);
      if (response?.data) {
        successToast(response.data.message || "City deleted successfully");
        payload?.handleFun();
        return payload?.id; // returning ID to remove from state
      }
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
